import { useState, useEffect, useCallback } from "react"
import ListPagination from '../common/ListPagination.js';
import { useNavigate } from "react-router-dom"
import { shipmentService } from "../../services/shipmentService"
import { locationHubService } from "../../services/locationHubService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import { Package, Plus, FileDown, BarChart3, X, QrCode } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import ListPageHeader from '../common/ListPageHeader.js';
import SearchableFilterSelect from '../common/SearchableFilterSelect.js';
import Button from '../common/Button.js'
import RowActionsMenu from '../common/RowActionsMenu.js'
import { showShipmentNetworkScopeTabs, hasShipmentCrossingAccess } from '../../utils/networkMode.js'
import { printShipmentReceiptChoice } from '../../utils/printShipmentReceipt.js'
import { printShipmentQrCode } from '../../utils/shipmentQrPrint.js'
import ReceiptPrintChooser from '../common/ReceiptPrintChooser.js'
import agentPartnershipService from "../../services/agentPartnershipService.js"
import { downloadBlob } from "../../utils/downloadBlob.js"
import { downloadPdfBlob, printPdfBlob, parseBlobErrorMessage } from "../../utils/printPdfBlob.js"
import { useTranslation } from 'react-i18next';
import { filterDateInputClass, listIdLinkMobileClass, listPageWrapperClass, listScopeTabClass, listScopeTabSmClass } from '../common/uiClasses.js';
import { filterShipmentLocations } from '../../utils/destinationType.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import { formatIndianDate, formatIndianTime, formatTimeAgo } from '../../utils/localDate.js';
import { isShipmentCancelled, isShipmentDeleted, shipmentDeletedRowClass, shipmentStatusLabel } from '../../utils/shipmentDeleted.js';
import DeletedShipmentInfoModal from './DeletedShipmentInfoModal.js';
import AmountAdjustRequestModal from './AmountAdjustRequestModal.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import { canAgentRequestShipmentEdit } from '../../utils/shipmentEditRequest.js';

const canEditShipmentRow = (shipment, user) => {
  if (shipment?.is_deleted) return false
  if (String(shipment?.status || "").toLowerCase() === "delivered") return false
  if (shipment?.can_edit != null) return Boolean(shipment.can_edit)
  if (shipment?.on_ml_sheet || shipment?.ml_sheet_id) {
    return user?.role === 'System Admin' || user?.role === 'Admin'
  }
  return user?.role !== 'Accountant'
}

const canOpenShipmentEditOrRequest = (shipment, user) => {
  if (isShipmentDeleted(shipment)) return false
  if (canEditShipmentRow(shipment, user)) return true
  return canAgentRequestShipmentEdit(shipment, user)
}

const FILTER_CATALOG_TTL_MS = 5 * 60 * 1000
const filterCatalog = { users: null, hubs: null, at: 0 }
const catalogFresh = () => Date.now() - filterCatalog.at < FILTER_CATALOG_TTL_MS

const ShipmentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useAuth()
  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)
  const [bulkQrDownloading, setBulkQrDownloading] = useState(false)
  const [docActionMessage, setDocActionMessage] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const canSeeReceiptPrintCount = user?.role === "Admin" || user?.role === "Accountant"
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    from_date: "",
    to_date: "",
    mode_of_payment: [],
    user_id: "",
    sender_location: [],
    receiver_location: [],
    sender_location_id: [],
    receiver_location_id: [],
  })
  const [users, setUsers] = useState([])
  const [openMenuId, setOpenMenuId] = useState(null)
  const [adjustShipment, setAdjustShipment] = useState(null)
  const [shipmentLocations, setShipmentLocations] = useState([])
  const [bookedByLocation, setBookedByLocation] = useState(null)
  const [bookedByLocationLoading, setBookedByLocationLoading] = useState(false)
  const [bookedByLocationOpen, setBookedByLocationOpen] = useState(false)
  const [breakdownTab, setBreakdownTab] = useState("location")
  const [listScope, setListScope] = useState("all")
  const [listFlow, setListFlow] = useState("outgoing")
  const [partnerCompanyId, setPartnerCompanyId] = useState("")
  const [partnerCompanies, setPartnerCompanies] = useState([])
  const [deletedInfoShipment, setDeletedInfoShipment] = useState(null)
  const [receiptPrintTarget, setReceiptPrintTarget] = useState(null)
  const showNetworkTabs = showShipmentNetworkScopeTabs(user)

  useEffect(() => {
    if (!showNetworkTabs && listScope !== "all") {
      setListScope("all")
      setPartnerCompanyId("")
    }
  }, [showNetworkTabs, listScope])

  useEffect(() => {
    if (user?.role === "Agent" && showNetworkTabs) {
      loadPartnerCompanies()
    }
  }, [user, showNetworkTabs])

  useEffect(() => {
    fetchUsers()
    fetchLocationHubs()
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest(".menu-container")) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [openMenuId])

  const normalizePaymentMode = (mode) =>
    String(mode || "").toLowerCase().replace(/\s+/g, "_")

  const buildSharedFilterParams = useCallback(() => {
    const params = {}
    if (filters.from_date) params.from_date = filters.from_date
    if (filters.to_date) params.to_date = filters.to_date
    if (filters.mode_of_payment?.length) {
      params.mode_of_payment = filters.mode_of_payment.map(normalizePaymentMode)
    }
    if (filters.sender_location?.length) params.sender_location = filters.sender_location
    if (filters.receiver_location?.length) params.receiver_location = filters.receiver_location
    if (filters.sender_location_id?.length) params.sender_location_id = filters.sender_location_id
    if (filters.receiver_location_id?.length) params.receiver_location_id = filters.receiver_location_id
    return params
  }, [
    filters.from_date,
    filters.to_date,
    filters.mode_of_payment,
    filters.sender_location,
    filters.receiver_location,
    filters.sender_location_id,
    filters.receiver_location_id,
  ])

  // Widget API supports dates + multi-select mode_of_payment + sender_location only
  const buildBookedByLocationParams = useCallback(() => {
    const params = {}
    if (filters.from_date) params.from_date = filters.from_date
    if (filters.to_date) params.to_date = filters.to_date
    if (filters.mode_of_payment?.length) {
      params.mode_of_payment = filters.mode_of_payment.map(normalizePaymentMode)
    }
    if (filters.sender_location?.length) params.sender_location = filters.sender_location
    return params
  }, [filters.from_date, filters.to_date, filters.mode_of_payment, filters.sender_location])

  const loadPartnerCompanies = async () => {
    try {
      const response = await agentPartnershipService.list({ status: "accepted" })
      const items = response.data?.data || []
      const unique = []
      const seen = new Set()
      items.forEach((p) => {
        if (!seen.has(p.requesting_company_id)) {
          seen.add(p.requesting_company_id)
          unique.push({
            id: p.requesting_company_id,
            name: p.requesting_company_name,
          })
        }
      })
      setPartnerCompanies(unique)
    } catch (error) {
      console.error("Failed to load partner companies", error)
    }
  }

  const buildFilterParams = useCallback((includePagination = true) => {
    const params = { ...buildSharedFilterParams() }
    if (includePagination) {
      params.page = currentPage
      params.per_page = 10
    }
    if (filters.status) params.status = filters.status
    if (filters.search) params.search = filters.search
    if (filters.user_id) params.user_id = filters.user_id
    if (showNetworkTabs && listScope !== "all") params.scope = listScope
    if (partnerCompanyId && listScope === "network") params.partner_company_id = partnerCompanyId
    if (listFlow && listFlow !== "all") params.flow = listFlow
    return params
  }, [buildSharedFilterParams, filters.status, filters.search, filters.user_id, currentPage, listScope, partnerCompanyId, showNetworkTabs, listFlow])

  const buildBreakdownParams = useCallback(() => {
    const params = buildBookedByLocationParams()
    if (showNetworkTabs && listScope !== "all") params.scope = listScope
    if (partnerCompanyId && listScope === "network") params.partner_company_id = partnerCompanyId
    return params
  }, [buildBookedByLocationParams, showNetworkTabs, listScope, partnerCompanyId])

  const fetchBreakdown = useCallback(async () => {
    try {
      setBookedByLocationLoading(true)
      const params = buildBreakdownParams()
      const response = breakdownTab === "agent" && showNetworkTabs
        ? await shipmentService.getBookedByAgent(params)
        : await shipmentService.getBookedByLocation(params)
      setBookedByLocation(response.data)
    } catch (error) {
      console.error("Error fetching booked breakdown:", error)
    } finally {
      setBookedByLocationLoading(false)
    }
  }, [breakdownTab, showNetworkTabs, buildBreakdownParams])

  const fetchShipments = useCallback(async () => {
    try {
      setLoading(true)
      const params = buildFilterParams()
      const response = await shipmentService.getShipments(params)
      setShipments((prev) => mergeLoadedPage(currentPage, response.data.shipments || [], prev))
      setTotalPages(response.data.pagination?.pages || 1)
    } catch (error) {
      console.error("Error fetching shipments:", error)
      Alert.error(error.response?.data?.error || "Failed to load shipments")
      setShipments([])
    } finally {
      setLoading(false)
    }
  }, [buildFilterParams])

  useEffect(() => {
    if (bookedByLocationOpen) {
      fetchBreakdown()
    }
  }, [bookedByLocationOpen, fetchBreakdown])

  useEffect(() => {
    fetchShipments()
  }, [fetchShipments])

  const fetchUsers = async () => {
    if (catalogFresh() && filterCatalog.users) {
      setUsers(filterCatalog.users)
      return
    }
    try {
      const response = await shipmentService.getDistinctUsers()
      const rows = (response.data.users || []).map((row) => ({
        ...row,
        location_hub_name: String(row.location_hub_name || "").trim(),
      }))
      filterCatalog.users = rows
      filterCatalog.at = Date.now()
      setUsers(rows)
    } catch (error) {
      console.error("Error fetching users:", error)
    }
  }

  const fetchLocationHubs = async () => {
    if (catalogFresh() && filterCatalog.hubs) {
      setShipmentLocations(filterCatalog.hubs)
      return
    }
    try {
      const response = await locationHubService.getAllHubs(1, 500)
      const data = response.data
      const hubs = filterShipmentLocations(data.hubs || data.items || data.location_hubs || [])
      filterCatalog.hubs = hubs
      filterCatalog.at = Date.now()
      setShipmentLocations(hubs)
    } catch (error) {
      console.error("Error fetching location hubs:", error)
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters({ ...filters, [name]: value })
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setFilters({
      status: "",
      search: "",
      from_date: "",
      to_date: "",
      mode_of_payment: [],
      user_id: "",
      sender_location: [],
      receiver_location: [],
      sender_location_id: [],
      receiver_location_id: [],
    })
    setListFlow("outgoing")
    setCurrentPage(1)
  }

  const hasActiveFilters =
    filters.status ||
    filters.search ||
    filters.from_date ||
    filters.to_date ||
    (filters.mode_of_payment?.length > 0) ||
    filters.user_id ||
    (filters.sender_location?.length > 0) ||
    (filters.receiver_location?.length > 0) ||
    (filters.sender_location_id?.length > 0) ||
    (filters.receiver_location_id?.length > 0) ||
    (listFlow && listFlow !== "outgoing")

  const getStatusColor = (status) => {
    const colors = {
      Booked: "bg-blue-100 text-blue-800 border-blue-200",
      "In Transit": "bg-indigo-100 text-indigo-800 border-indigo-200",
      Dispatched: "bg-purple-100 text-purple-800 border-purple-200",
      Received: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Delivered: "bg-green-100 text-green-800 border-green-200",
      Returned: "bg-red-100 text-red-800 border-red-200",
      Rejected: "bg-red-100 text-red-800 border-red-200",
      Cancelled: "bg-red-100 text-red-800 border-red-200",
    }
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  const toggleMultiSelect = (field, value) => {
    const normalizedValue = field === "mode_of_payment" ? normalizePaymentMode(value) : value
    setFilters((prev) => {
      const current = prev[field] || []
      const exists = current.some((v) => String(v) === String(normalizedValue))
      const updated = exists
        ? current.filter((v) => String(v) !== String(normalizedValue))
        : [...current, normalizedValue]
      return { ...prev, [field]: updated }
    })
    setCurrentPage(1)
  }

  const handleExportPDF = async () => {
    try {
      const exportParams = buildFilterParams(false)
      const response = await shipmentService.exportPDF(exportParams)
      await downloadBlob(
        response.data,
        `shipments_${new Date().toISOString().split("T")[0]}.pdf`,
        "application/pdf",
      )
      Alert.success("PDF exported successfully")
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to export PDF"))
    }
  }

  const handleBulkDownloadQR = async () => {
    try {
      setBulkQrDownloading(true)
      const params = buildFilterParams(false)
      const response = await shipmentService.bulkDownloadQRCodesFiltered(params)
      await downloadBlob(
        response.data,
        `shipment_qr_codes_${new Date().toISOString().split("T")[0]}.pdf`,
        "application/pdf",
      )
      Alert.success("QR codes PDF downloaded")
    } catch (error) {
      const message = await parseBlobErrorMessage(
        error,
        "Failed to download QR codes"
      )
      Alert.error(message)
    } finally {
      setBulkQrDownloading(false)
    }
  }

  const toggleMenu = (shipmentId) => {
    setOpenMenuId(openMenuId === shipmentId ? null : shipmentId)
  }

  const handleDownloadInvoice = async (shipment) => {
    try {
      setOpenMenuId(null)
      setDocActionMessage("Preparing invoice...")
      const response = await shipmentService.downloadInvoice(shipment.shipment_id)
      await downloadPdfBlob(response.data, `invoice_${shipment.shipment_id}.pdf`)
      Alert.success("Invoice downloaded successfully")
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to download invoice"))
    } finally {
      setDocActionMessage("")
    }
  }

  const handlePrintInvoice = async (shipment) => {
    try {
      setOpenMenuId(null)
      setDocActionMessage("Preparing invoice...")
      const response = await shipmentService.downloadInvoice(shipment.shipment_id)
      const result = await printPdfBlob(response.data, `invoice_${shipment.shipment_id}.pdf`)
      if (result.method === 'blocked') {
        Alert.error('Could not open the print dialog')
      }
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to print invoice"))
    } finally {
      setDocActionMessage("")
    }
  }

  const handlePrintReceipt = (shipment) => {
    setOpenMenuId(null)
    setReceiptPrintTarget(shipment)
  }

  const handleReceiptLayoutSelect = async (layout) => {
    const shipment = receiptPrintTarget
    if (!shipment) return
    setReceiptPrintTarget(null)
    setDocActionMessage("Preparing receipt...")
    try {
      const st = String(shipment?.status || '').toLowerCase()
      const event = st === 'delivered' ? 'delivery' : 'booking'
      await printShipmentReceiptChoice(shipment.shipment_id, user, layout, event)
      if (canSeeReceiptPrintCount) {
        fetchShipments()
      } else {
        setShipments((prev) =>
          prev.map((s) =>
            s.shipment_id === shipment.shipment_id
              ? { ...s, receipt_print_count: (s.receipt_print_count || 0) + 1 }
              : s
          )
        )
      }
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to print receipt")
    } finally {
      setDocActionMessage("")
    }
  }

  const handlePrintEwayBill = async (shipment) => {
    try {
      setOpenMenuId(null)
      const response = await shipmentService.printEwayBill(shipment.shipment_id)
      const result = await printPdfBlob(response.data, `eway_bill_${shipment.shipment_id}.pdf`, { allowDownloadFallback: true })
      if (result.method === 'download') {
        Alert.success('Popup blocked — e-Way Bill PDF downloaded. Open the file to print.')
      }
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to print e-Way Bill"))
    }
  }

  const handlePrintQR = async (shipment) => {
    try {
      setOpenMenuId(null)
      setDocActionMessage("Preparing QR code...")
      await printShipmentQrCode(shipment.shipment_id)
    } finally {
      setDocActionMessage("")
    }
  }

  const openShipmentFromList = (shipment) => {
    if (isShipmentDeleted(shipment)) {
      setDeletedInfoShipment(shipment)
      return
    }
    navigate(`/shipments/view/${shipment.shipment_id}`)
  }

  const handleDeleteShipment = async (shipment) => {
    try {
      setOpenMenuId(null)
      if (isShipmentDeleted(shipment)) {
        setDeletedInfoShipment(shipment)
        return
      }
      const reason = await Alert.prompt(
        `Enter a reason to delete shipment ${shipment.shipment_id}. The record will stay on this list as crossed out.`,
        "Delete Shipment",
        { minLength: 3, confirmLabel: "Delete", placeholder: "Reason is required" }
      )
      if (reason === null) return

      await shipmentService.deleteShipment(shipment.shipment_id, reason)
      Alert.success("Shipment marked as deleted")
      
      fetchShipments()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to delete shipment")
    }
  }

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
                <ListPageHeader title="Shipments" icon={Package} className="mb-2 sm:mb-3">
                    {user?.role !== "Accountant" && (
                      <Button
                        mobileIconOnly
                        variant="primary"
                        icon={Plus}
                        onClick={() => navigate("/shipments/add")}
                      >
                        Add Shipment
                      </Button>
                    )}
                    <Button
                      mobileIconOnly
                      variant="secondary"
                      icon={QrCode}
                      loading={bulkQrDownloading}
                      disabled={bulkQrDownloading}
                      onClick={handleBulkDownloadQR}
                    >
                      Download QR Codes
                    </Button>
                    {(user?.role === "System Admin" || user?.role === "Admin") && (
                      <Button
                        mobileIconOnly
                        variant="secondary"
                        icon={FileDown}
                        onClick={handleExportPDF}
                      >
                        Export PDF
                      </Button>
                    )}
                    <Button
                      mobileIconOnly
                      variant={bookedByLocationOpen ? "primary" : "secondary"}
                      icon={BarChart3}
                      onClick={() => setBookedByLocationOpen((open) => !open)}
                      mobileLabel={bookedByLocationOpen ? "Hide Breakdown" : "Booked Breakdown"}
                    >
                      {bookedByLocationOpen ? "Hide Breakdown" : "Booked Breakdown"}
                    </Button>
                </ListPageHeader>

                {showNetworkTabs && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {[
                      { id: "all", label: "All" },
                      { id: "company", label: "Company" },
                      { id: "network", label: "Network" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => { setListScope(tab.id); setCurrentPage(1); }}
                        className={listScopeTabClass(listScope === tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}

                {showNetworkTabs && user?.role === "Agent" && listScope === "network" && partnerCompanies.length > 0 && (
                  <div className="mb-4">
                    <select
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                      value={partnerCompanyId}
                      onChange={(e) => { setPartnerCompanyId(e.target.value); setCurrentPage(1); }}
                    >
                      <option value="">All partner companies</option>
                      {partnerCompanies.map((company) => (
                        <option key={company.id} value={company.id}>{company.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Booked Shipments Breakdown — hidden by default, opened from header button */}
                {bookedByLocationOpen && (
                <div className="bg-white rounded-xl mb-4 shadow-md border border-blue-100 overflow-hidden">
                  <div className="px-4 py-4 bg-gradient-to-r from-blue-50 via-white to-white border-b border-blue-100">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <BarChart3 className="w-5 h-5 text-blue-500 shrink-0" />
                        <h2 className="font-semibold text-gray-800 text-sm sm:text-base">
                          Booked Shipments Breakdown
                        </h2>
                      </div>
                      <button
                        type="button"
                        onClick={() => setBookedByLocationOpen(false)}
                        className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 shrink-0"
                        aria-label="Close breakdown"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    {bookedByLocation?.summary && (
                      <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap">
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-[11px] sm:text-xs font-bold border border-blue-200 text-center md:text-left">
                          {bookedByLocation.summary.total_booked_shipments} Booked
                        </span>
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-[11px] sm:text-xs font-bold border border-blue-200 text-center md:text-left">
                          ₹{bookedByLocation.summary.total_charges?.toLocaleString("en-IN")} Total
                        </span>
                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-[11px] sm:text-xs font-bold border border-green-200 text-center md:text-left">
                          ₹{bookedByLocation.summary.total_paid?.toLocaleString("en-IN")} Paid
                        </span>
                        <span className="px-2 py-1 rounded-full bg-red-100 text-red-800 text-[11px] sm:text-xs font-bold border border-red-200 text-center md:text-left">
                          ₹{(bookedByLocation.summary.to_pay ?? bookedByLocation.summary.total_balance)?.toLocaleString("en-IN")} To Pay
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="px-4 pb-4">
                      {showNetworkTabs && (
                        <div className="flex flex-wrap gap-2 py-3 border-b border-gray-100">
                          {[
                            { id: "location", label: "By Sender Location" },
                            { id: "agent", label: "By Agent" },
                          ].map((tab) => (
                            <button
                              key={tab.id}
                              type="button"
                              onClick={() => setBreakdownTab(tab.id)}
                              className={listScopeTabSmClass(breakdownTab === tab.id)}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      )}

                      {bookedByLocationLoading ? (
                        <div className="py-8 text-center text-sm text-gray-500">Loading breakdown…</div>
                      ) : bookedByLocation?.data?.length ? (
                        <>
                          {bookedByLocation?.summary?.payment_modes && (
                            <div className="grid grid-cols-2 gap-2 py-3 border-b border-gray-100 md:flex md:flex-wrap md:items-center">
                              <span className="text-xs text-gray-500 self-center col-span-2 md:col-auto md:mr-1">Filter by payment:</span>
                              {Object.entries(bookedByLocation.summary.payment_modes).map(([mode, count]) => {
                                const modeValue = normalizePaymentMode(mode)
                                return (
                                  <span
                                    key={mode}
                                    onClick={() => toggleMultiSelect("mode_of_payment", modeValue)}
                                    className={`px-2 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold border cursor-pointer transition-all text-center md:text-left ${
                                      filters.mode_of_payment?.includes(modeValue)
                                        ? "bg-buttonBg text-white border-buttonBg"
                                        : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-blue-50 hover:border-blue-200"
                                    }`}
                                  >
                                    {mode.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}: {count}
                                  </span>
                                )
                              })}
                            </div>
                          )}

                          {/* Mobile — location/agent cards */}
                          <div className="md:hidden space-y-2 pt-3">
                            {breakdownTab === "agent" && showNetworkTabs
                              ? bookedByLocation.data.map((row) => (
                                <div key={row.agent_user_id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                                  <p className="font-semibold text-gray-800 text-sm">{row.agent_name}</p>
                                  <p className="text-gray-500 text-xs mb-2">{row.agent_user_id}{row.agent_city ? ` · ${row.agent_city}` : ""}</p>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div><span className="text-gray-500 block">Shipments</span><span className="font-semibold">{row.total_shipments}</span></div>
                                    <div><span className="text-gray-500 block">Total</span><span className="font-semibold">₹{row.total_charges?.toLocaleString("en-IN")}</span></div>
                                    <div><span className="text-gray-500 block">Paid</span><span className="font-semibold text-green-700">₹{row.total_paid?.toLocaleString("en-IN")}</span></div>
                                    <div><span className="text-gray-500 block">To Pay</span><span className="font-semibold text-red-700">₹{(row.to_pay ?? row.total_balance)?.toLocaleString("en-IN")}</span></div>
                                  </div>
                                </div>
                              ))
                              : bookedByLocation.data.map((loc) => (
                                <div
                                  key={loc.sender_location}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    setFilters((f) => {
                                      const current = f.sender_location || []
                                      const isSelected = current.includes(loc.sender_location)
                                      return {
                                        ...f,
                                        sender_location: isSelected
                                          ? current.filter((v) => v !== loc.sender_location)
                                          : [...current, loc.sender_location],
                                        status: isSelected ? "" : "Booked",
                                      }
                                    })
                                    setCurrentPage(1)
                                  }}
                                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.currentTarget.click() }}
                                  className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                                    filters.sender_location?.includes(loc.sender_location)
                                      ? "border-blue-300 bg-blue-50"
                                      : "border-gray-100 bg-gray-50/60 hover:bg-blue-50/60"
                                  }`}
                                >
                                  <p className="font-semibold text-gray-800 text-sm uppercase">{loc.sender_location}</p>
                                  <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                                    <div><span className="text-gray-500 block">Shipments</span><span className="font-semibold">{loc.total_shipments}</span></div>
                                    <div><span className="text-gray-500 block">Total</span><span className="font-semibold">₹{loc.total_charges?.toLocaleString("en-IN")}</span></div>
                                    <div><span className="text-gray-500 block">Paid</span><span className="font-semibold text-green-700">₹{loc.total_paid?.toLocaleString("en-IN")}</span></div>
                                    <div><span className="text-gray-500 block">To Pay</span><span className="font-semibold text-red-700">₹{(loc.to_pay ?? loc.total_balance)?.toLocaleString("en-IN")}</span></div>
                                  </div>
                                </div>
                              ))}
                            {bookedByLocation.summary && (
                              <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-3 font-bold text-xs sm:text-sm">
                                <p className="mb-2">Grand Total</p>
                                <div className="grid grid-cols-2 gap-2 font-semibold">
                                  <div><span className="text-gray-600 block font-medium">Shipments</span>{bookedByLocation.summary.total_booked_shipments}</div>
                                  <div><span className="text-gray-600 block font-medium">Total</span>₹{bookedByLocation.summary.total_charges?.toLocaleString("en-IN")}</div>
                                  <div><span className="text-gray-600 block font-medium">Paid</span><span className="text-green-800">₹{bookedByLocation.summary.total_paid?.toLocaleString("en-IN")}</span></div>
                                  <div><span className="text-gray-600 block font-medium">To Pay</span><span className="text-red-800">₹{(bookedByLocation.summary.to_pay ?? bookedByLocation.summary.total_balance)?.toLocaleString("en-IN")}</span></div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Tablet / desktop — table */}
                          <div className="hidden md:block overflow-x-auto pt-3">
                            <table className="min-w-full text-xs sm:text-sm">
                              <thead>
                                <tr className="border-b bg-gray-50 text-left text-gray-600">
                                  <th className="py-2.5 px-3 font-semibold">{breakdownTab === "agent" && showNetworkTabs ? "Agent" : "Sender Location"}</th>
                                  <th className="py-2.5 px-3 text-right font-semibold">{t('pages.shipment.title')}</th>
                                  <th className="py-2.5 px-3 text-right font-semibold">Total</th>
                                  <th className="py-2.5 px-3 text-right font-semibold">Paid</th>
                                  <th className="py-2.5 px-3 text-right font-semibold">To Pay</th>
                                </tr>
                              </thead>
                              <tbody>
                                {breakdownTab === "agent" && showNetworkTabs
                                  ? bookedByLocation.data.map((row) => (
                                    <tr key={row.agent_user_id} className="border-b hover:bg-blue-50/60">
                                      <td className="py-2.5 px-3">
                                        <div className="font-semibold text-gray-800">{row.agent_name}</div>
                                        <div className="text-gray-500 text-xs">{row.agent_user_id}{row.agent_city ? ` · ${row.agent_city}` : ""}</div>
                                      </td>
                                      <td className="py-2.5 px-3 text-right">{row.total_shipments}</td>
                                      <td className="py-2.5 px-3 text-right">₹{row.total_charges?.toLocaleString("en-IN")}</td>
                                      <td className="py-2.5 px-3 text-right text-green-700">₹{row.total_paid?.toLocaleString("en-IN")}</td>
                                      <td className="py-2.5 px-3 text-right text-red-700">₹{(row.to_pay ?? row.total_balance)?.toLocaleString("en-IN")}</td>
                                    </tr>
                                  ))
                                  : bookedByLocation.data.map((loc) => (
                                    <tr
                                      key={loc.sender_location}
                                      className={`border-b cursor-pointer hover:bg-blue-50/60 ${filters.sender_location?.includes(loc.sender_location) ? "bg-blue-100/80" : ""}`}
                                      onClick={() => {
                                        setFilters((f) => {
                                          const current = f.sender_location || []
                                          const isSelected = current.includes(loc.sender_location)
                                          return {
                                            ...f,
                                            sender_location: isSelected
                                              ? current.filter((v) => v !== loc.sender_location)
                                              : [...current, loc.sender_location],
                                            status: isSelected ? "" : "Booked",
                                          }
                                        })
                                        setCurrentPage(1)
                                      }}
                                    >
                                      <td className="py-2.5 px-3 font-semibold uppercase">{loc.sender_location}</td>
                                      <td className="py-2.5 px-3 text-right">{loc.total_shipments}</td>
                                      <td className="py-2.5 px-3 text-right">₹{loc.total_charges?.toLocaleString("en-IN")}</td>
                                      <td className="py-2.5 px-3 text-right text-green-700">₹{loc.total_paid?.toLocaleString("en-IN")}</td>
                                      <td className="py-2.5 px-3 text-right text-red-700">₹{(loc.to_pay ?? loc.total_balance)?.toLocaleString("en-IN")}</td>
                                    </tr>
                                  ))}
                                {bookedByLocation.summary && (
                                  <tr className="bg-blue-50 font-bold border-t-2 border-blue-200">
                                    <td className="py-2.5 px-3">Grand Total</td>
                                    <td className="py-2.5 px-3 text-right">{bookedByLocation.summary.total_booked_shipments}</td>
                                    <td className="py-2.5 px-3 text-right">₹{bookedByLocation.summary.total_charges?.toLocaleString("en-IN")}</td>
                                    <td className="py-2.5 px-3 text-right text-green-800">₹{bookedByLocation.summary.total_paid?.toLocaleString("en-IN")}</td>
                                    <td className="py-2.5 px-3 text-right text-red-800">₹{(bookedByLocation.summary.to_pay ?? bookedByLocation.summary.total_balance)?.toLocaleString("en-IN")}</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <p className="text-xs text-gray-400 mt-3">
                            {breakdownTab === "location" ? "Click a sender location row to filter the shipment list below." : "Breakdown by booking agent for the selected date range."}
                          </p>
                        </>
                      ) : (
                        <div className="py-8 text-center text-sm text-gray-500">No booked shipments for this period</div>
                      )}
                  </div>
                </div>
                )}

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={handleClearFilters}>
                    <div className="col-span-2 lg:col-span-1 min-w-0">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
                      <SearchSubmitInput
                        name="search"
                        placeholder="GC, CN, consigner, consignee..."
                        ariaLabel="Search shipments"
                        value={filters.search}
                        onSearch={(value) => {
                          setFilters((current) => ({ ...current, search: value }))
                          setCurrentPage(1)
                        }}
                        inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      />
                    </div>
                    <div className="col-span-2 lg:col-span-1 min-w-0">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Direction</label>
                      <div className="flex w-full min-w-0 h-[42px] rounded-xl border border-gray-300 overflow-hidden">
                        {['all', 'incoming', 'outgoing'].map((flow) => (
                          <button
                            key={flow}
                            type="button"
                            onClick={() => { setListFlow(flow); setCurrentPage(1) }}
                            className={`flex-1 min-w-0 h-full px-1 sm:px-2 text-xs sm:text-sm font-medium whitespace-nowrap ${
                              listFlow === flow ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {flow === 'all' ? 'All' : flow === 'incoming' ? 'Incoming' : 'Outgoing'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                      <select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                        className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      >
                        <option value="">All Statuses</option>
                        <option value="Booked">Booked</option>
                        <option value="In Transit">In Transit</option>
                        <option value="Dispatched">Dispatched</option>
                        <option value="Received">Received</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Returned">Returned</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Payment Mode
                      </label>
                      <SearchableFilterSelect
                        multiple
                        name="mode_of_payment"
                        value={filters.mode_of_payment || []}
                        placeholder="All Payment Modes"
                        onChange={(e) => {
                          setFilters((f) => ({ ...f, mode_of_payment: e.target.value }))
                          setCurrentPage(1)
                        }}
                        options={[
                          { value: "to_pay", label: "To Pay" },
                          { value: "credit", label: "Credit" },
                          { value: "paid", label: "Paid" },
                        ]}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">From Date</label>
                      <input
                        type="date"
                        name="from_date"
                        value={filters.from_date}
                        onChange={handleFilterChange}
                        className={filterDateInputClass}
                        aria-label="From date"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">To Date</label>
                      <input
                        type="date"
                        name="to_date"
                        value={filters.to_date}
                        onChange={handleFilterChange}
                        className={filterDateInputClass}
                        aria-label="To date"
                      />
                    </div>
                    {(user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant") && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">User</label>
                        <select
                          name="user_id"
                          value={filters.user_id}
                          onChange={handleFilterChange}
                          className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          <option value="">All Users</option>
                          {users.map((u) => (
                            <option key={u.user_id} value={u.user_id}>
                              {u.location_hub_name ? `${u.name} (${u.location_hub_name})` : u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Sender Location</label>
                        <SearchableFilterSelect
                          multiple
                          name="sender_location_id"
                          value={filters.sender_location_id || []}
                          placeholder="All Sender Locations"
                          onChange={(e) => {
                            setFilters((f) => ({ ...f, sender_location_id: e.target.value }))
                            setCurrentPage(1)
                          }}
                          options={sortLocationsAsc(shipmentLocations).map((loc) => ({
                            value: loc.id,
                            label: formatLocationDropdownLabel(loc),
                          }))}
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Receiver Location</label>
                        <SearchableFilterSelect
                          multiple
                          name="receiver_location_id"
                          value={filters.receiver_location_id || []}
                          placeholder="All Receiver Locations"
                          onChange={(e) => {
                            setFilters((f) => ({ ...f, receiver_location_id: e.target.value }))
                            setCurrentPage(1)
                          }}
                          options={sortLocationsAsc(shipmentLocations).map((loc) => ({
                            value: loc.id,
                            label: formatLocationDropdownLabel(loc),
                          }))}
                        />
                      </div>
                </ListFilterPanel>

                {/* Shipments Table - Desktop */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 w-full min-w-0 max-w-full">
                  {loading && currentPage === 1 ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader fullScreen={false} size="default" message="Loading shipments..." />
                    </div>
                  ) : shipments.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="text-6xl mb-4">🔭</div>
                      <p className="text-gray-600 text-lg font-semibold">No shipments found</p>
                      <p className="text-gray-500 mt-2">Try adjusting your filters or create a new shipment</p>
                    </div>
                  ) : (
                    <>
                      <div className="hidden xl:block w-full min-w-0">
                        <table className="w-full table-fixed border-collapse [&_th]:min-w-0 [&_td]:min-w-0">
                          <thead className="bg-gray-50 rounded-t-xl">
                            <tr className="border-b border-gray-200">
                              <th className="w-[16%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                                GC Number
                              </th>
                              <th className="w-[14%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Consigner</th>
                              <th className="w-[14%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Consignee</th>
                              <th className="w-[10%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Payment Mode</th>
                              <th className="w-[8%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                              <th className="w-[11%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Amount</th>
                              <th className="w-[11%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Balance</th>
                              <th className="w-[10%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Date</th>
                              <th className="w-[6%] px-3 2xl:px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {shipments.map((shipment, index) => (
                              <tr
                                key={shipment.id}
                                className={shipmentDeletedRowClass(
                                  isShipmentCancelled(shipment) ? null : shipment,
                                  `hover:bg-gray-50 relative transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-50"} ${isShipmentDeleted(shipment) && !isShipmentCancelled(shipment) ? "cursor-pointer" : ""}`
                                )}
                                onClick={isShipmentDeleted(shipment) && !isShipmentCancelled(shipment) ? () => openShipmentFromList(shipment) : undefined}
                              >
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                                  <a
                                    href={`/shipments/view/${encodeURIComponent(shipment.shipment_id)}`}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      openShipmentFromList(shipment)
                                    }}
                                    title={shipment.shipment_id}
                                    className="font-regular text-secondary hover:text-secondary hover:underline cursor-pointer break-words"
                                  >
                                    {shipment.shipment_id}
                                  </a>
                                  {shipment.consignment_no && (
                                    <div className="text-[11px] text-gray-900 font-medium mt-0.5 break-words">
                                      CN: {shipment.consignment_no}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                                  <div className="font-regular text-gray-900 whitespace-normal break-words">
                                    {shipment.consigner_name || shipment.sender_name}
                                  </div>
                                  <div className="font-regular text-gray-900 whitespace-normal break-words">
                                    {shipment.consigner_contact || shipment.sender_contact}
                                  </div>
                                  {shipment.sender_location && (
                                    <div className="text-xs text-gray-400 mt-0.5 whitespace-normal break-words">📍 {shipment.sender_location}</div>
                                  )}
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                                  <div className="font-regular text-gray-900 whitespace-normal break-words">
                                    {shipment.consignee_name || shipment.receiver_name}
                                  </div>
                                  <div className="font-regular text-gray-900 whitespace-normal break-words">
                                    {shipment.consignee_contact || shipment.receiver_contact}
                                  </div>
                                  {shipment.receiver_location && (
                                    <div className="text-xs text-gray-400 mt-0.5 whitespace-normal break-words">📍 {shipment.receiver_location}</div>
                                  )}
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                                  <span className="font-regular text-gray-900 uppercase whitespace-nowrap">
                                    {shipment.mode_of_payment ? shipment.mode_of_payment.replace(/_/g, ' ') : 'N/A'}
                                  </span>
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top overflow-hidden">
                                  <div className="uppercase text-gray-900 break-words leading-5">
                                    {shipmentStatusLabel(shipment)}
                                  </div>
                                  {isShipmentDeleted(shipment) && !isShipmentCancelled(shipment) ? (
                                    <div className="mt-0.5 text-[11px] font-semibold uppercase leading-4 text-red-600 break-words">
                                      Deleted
                                    </div>
                                  ) : null}
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top whitespace-nowrap">
                                  <span className="font-regular text-gray-900">
                                    ₹{shipment.total_amount?.toFixed(2)}
                                  </span>
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top whitespace-nowrap">
                                  <span className="font-regular text-base text-gray-900">
                                    ₹{shipment.balance_amount?.toFixed(2)}
                                  </span>
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top text-gray-900 leading-5">
                                  <div className="whitespace-nowrap">{formatIndianDate(shipment.created_at)}</div>
                                  <div className="whitespace-nowrap">{formatIndianTime(shipment.created_at)}</div>
                                  {formatTimeAgo(shipment.created_at) ? (
                                    <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">{formatTimeAgo(shipment.created_at)}</div>
                                  ) : null}
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm text-center align-top" onClick={(e) => e.stopPropagation()}>
                                  <RowActionsMenu
                                    className="relative menu-container"
                                    isOpen={openMenuId === shipment.shipment_id}
                                    onToggle={() => toggleMenu(shipment.shipment_id)}
                                    onClose={() => setOpenMenuId(null)}
                                    editFirst
                                    onView={() => openShipmentFromList(shipment)}
                                    onEdit={canOpenShipmentEditOrRequest(shipment, user)
                                      ? () => navigate(`/shipments/edit/${encodeURIComponent(shipment.shipment_id)}`)
                                      : undefined}
                                    editLabel={user?.role === 'Agent' ? 'Request update' : undefined}
                                    onDelete={
                                      !isShipmentDeleted(shipment) && (user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant')
                                        ? () => handleDeleteShipment(shipment)
                                        : undefined
                                    }
                                    extraItems={isShipmentDeleted(shipment) ? [] : [
                                      ...(user?.role === 'Agent' && String(shipment.status || '').toLowerCase() !== 'delivered'
                                        ? [{ label: 'Adjust amount', icon: '₹', onClick: () => setAdjustShipment(shipment) }]
                                        : []),
                                      ...(hasShipmentCrossingAccess(user) && ['Dispatched', 'Received'].includes(shipment.status)
                                        ? [{ label: 'Cross Shipment', icon: '🔀', onClick: () => navigate(`/shipment-crossing?shipment_id=${shipment.shipment_id}`) }]
                                        : []),
                                      { label: 'Download Invoice', icon: '📄', onClick: () => handleDownloadInvoice(shipment) },
                                      { label: 'Print Invoice', icon: '🖨️', onClick: () => handlePrintInvoice(shipment) },
                                      { label: 'Print Receipt', icon: '🖨️', onClick: () => handlePrintReceipt(shipment) },
                                      ...((shipment.eway_bill_numbers || []).length
                                        ? [{ label: 'Print e-Way Bill', icon: '🖨️', onClick: () => handlePrintEwayBill(shipment) }]
                                        : []),
                                      { label: 'Print QR Code', icon: '🔳', onClick: () => handlePrintQR(shipment) },
                                    ]}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Tablet & Mobile cards (inside same panel) */}
                      <div className="xl:hidden space-y-3 p-4">
                        {shipments.map((shipment) => (
                          <div
                            key={`card-${shipment.id}`}
                            className={shipmentDeletedRowClass(
                              isShipmentCancelled(shipment) ? null : shipment,
                              `bg-white rounded-xl shadow-sm border border-gray-100 p-4 border-l-4 ${isShipmentDeleted(shipment) && !isShipmentCancelled(shipment) ? "border-l-gray-400 cursor-pointer" : "border-l-blue-500"}`
                            )}
                            onClick={isShipmentDeleted(shipment) && !isShipmentCancelled(shipment) ? () => openShipmentFromList(shipment) : undefined}
                          >
                            <div className="mb-3 space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <a
                                    href={`/shipments/view/${encodeURIComponent(shipment.shipment_id)}`}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      openShipmentFromList(shipment)
                                    }}
                                    title={`#${shipment.shipment_id}`}
                                    className={`${listIdLinkMobileClass} block truncate`}
                                  >
                                    #{shipment.shipment_id}
                                  </a>
                                  {shipment.consignment_no && (
                                    <p className="text-[11px] text-gray-900 font-medium mt-0.5">CN: {shipment.consignment_no}</p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-0.5 leading-4">
                                    {formatIndianDate(shipment.created_at)}
                                    {formatIndianTime(shipment.created_at) ? ` · ${formatIndianTime(shipment.created_at)}` : ''}
                                    {formatTimeAgo(shipment.created_at) ? ` · ${formatTimeAgo(shipment.created_at)}` : ''}
                                  </p>
                                </div>
                                <div onClick={(e) => e.stopPropagation()}>
                                <RowActionsMenu
                                  className="relative menu-container shrink-0"
                                  isOpen={openMenuId === `card-${shipment.shipment_id}`}
                                  onToggle={() => toggleMenu(`card-${shipment.shipment_id}`)}
                                  onClose={() => setOpenMenuId(null)}
                                  editFirst
                                  onView={() => openShipmentFromList(shipment)}
                                  onEdit={canOpenShipmentEditOrRequest(shipment, user)
                                    ? () => navigate(`/shipments/edit/${encodeURIComponent(shipment.shipment_id)}`)
                                    : undefined}
                                  editLabel={user?.role === 'Agent' ? 'Request update' : undefined}
                                  onDelete={
                                    !isShipmentDeleted(shipment) && (user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant')
                                      ? () => handleDeleteShipment(shipment)
                                      : undefined
                                  }
                                  extraItems={isShipmentDeleted(shipment) ? [] : [
                                    ...(user?.role === 'Agent' && String(shipment.status || '').toLowerCase() !== 'delivered'
                                      ? [{ label: 'Adjust amount', icon: '₹', onClick: () => setAdjustShipment(shipment) }]
                                      : []),
                                    ...(hasShipmentCrossingAccess(user) && ['Dispatched', 'Received'].includes(shipment.status)
                                      ? [{ label: 'Cross Shipment', icon: '🔀', onClick: () => navigate(`/shipment-crossing?shipment_id=${shipment.shipment_id}`) }]
                                      : []),
                                    { label: 'Download Invoice', icon: '📄', onClick: () => handleDownloadInvoice(shipment) },
                                    { label: 'Print Invoice', icon: '🖨️', onClick: () => handlePrintInvoice(shipment) },
                                    { label: 'Print Receipt', icon: '🖨️', onClick: () => handlePrintReceipt(shipment) },
                                    ...((shipment.eway_bill_numbers || []).length
                                      ? [{ label: 'Print e-Way Bill', icon: '🖨️', onClick: () => handlePrintEwayBill(shipment) }]
                                      : []),
                                    { label: 'Print QR Code', icon: '🔳', onClick: () => handlePrintQR(shipment) },
                                  ]}
                                />
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 uppercase">
                                  {shipment.mode_of_payment ? shipment.mode_of_payment.replace(/_/g, ' ') : 'N/A'}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getStatusColor(shipmentStatusLabel(shipment))}`}>
                                  {isShipmentDeleted(shipment) && !isShipmentCancelled(shipment) ? "Deleted" : shipmentStatusLabel(shipment)}
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="min-w-0">
                                <p className="text-xs text-gray-500 font-medium">Consigner</p>
                                <p className="font-semibold text-gray-800 break-words">{shipment.consigner_name || shipment.sender_name}</p>
                                {shipment.sender_location && (
                                  <p className="text-[11px] text-gray-400 break-words">📍 {shipment.sender_location}</p>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs text-gray-500 font-medium">Consignee</p>
                                <p className="font-semibold text-gray-800 break-words">{shipment.consignee_name || shipment.receiver_name}</p>
                                {shipment.receiver_location && (
                                  <p className="text-[11px] text-gray-400 break-words">📍 {shipment.receiver_location}</p>
                                )}
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 font-medium">Amount</p>
                                <p className="font-bold text-gray-900">₹{shipment.total_amount?.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 font-medium">Balance</p>
                                <p className={`font-bold ${shipment.balance_amount > 0 ? "text-red-600" : "text-green-600"}`}>
                                  ₹{shipment.balance_amount?.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}

                      </div>

                      <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        loading={loading && currentPage > 1}
                        onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
                      />
                    </>
                  )}
                </div>
      </div>
      {deletedInfoShipment ? (
        <DeletedShipmentInfoModal
          shipment={deletedInfoShipment}
          onClose={() => setDeletedInfoShipment(null)}
        />
      ) : null}
      <AmountAdjustRequestModal
        shipment={adjustShipment}
        onClose={() => setAdjustShipment(null)}
      />
      {docActionMessage ? <Loader overlay message={docActionMessage} /> : null}
      <ReceiptPrintChooser
        open={!!receiptPrintTarget}
        shipmentId={receiptPrintTarget?.shipment_id}
        onClose={() => setReceiptPrintTarget(null)}
        onSelect={handleReceiptLayoutSelect}
      />
    </DashboardLayout>
  )
}

export default ShipmentList;